import * as pty from "node-pty";
import type { IPty } from "node-pty";
import type { ProviderAdapter, ProviderHookContext } from "../providers/index.js";
import { readTtySize, restoreHostTerminalModes } from "./tty.js";
import { prepareCommandLaunch } from "../common/command-launch.js";
import { createPtyInputCompat } from "../common/pty-input-compat.js";

interface PtyManagerOptions {
  provider: ProviderAdapter;
  providerArgs: string[];
  cwd: string;
  initialSize?: { cols: number; rows: number };
  hook?: ProviderHookContext;
  tap: (data: string) => void;
  onInput?: (data: string) => void;
  stdin: NodeJS.ReadStream;
  stdout: NodeJS.WriteStream;
  onSessionExit?: (code: number) => void | Promise<void>;
  onResize?: (cols: number, rows: number) => void;
}

export class PtyManager {
  private child: IPty | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly provider: ProviderAdapter;
  private readonly inputCompat: ReturnType<typeof createPtyInputCompat>;
  private readonly providerArgs: string[];
  private readonly cwd: string;
  private readonly initialSize?: { cols: number; rows: number };
  private readonly hook?: ProviderHookContext;
  private readonly tap: (data: string) => void;
  private readonly onInput?: (data: string) => void;
  private readonly stdin: NodeJS.ReadStream;
  private readonly stdout: NodeJS.WriteStream;
  private readonly onSessionExit?: (code: number) => void;
  private readonly onResize?: (cols: number, rows: number) => void;

  constructor(options: PtyManagerOptions) {
    this.provider = options.provider;
    this.inputCompat = createPtyInputCompat(options.provider.id);
    this.providerArgs = options.providerArgs;
    this.cwd = options.cwd;
    this.initialSize = options.initialSize;
    this.hook = options.hook;
    this.tap = options.tap;
    this.onInput = options.onInput;
    this.stdin = options.stdin;
    this.stdout = options.stdout;
    this.onSessionExit = options.onSessionExit;
    this.onResize = options.onResize;
  }

  start(): void {
    const { cols, rows } = this.initialSize ?? readTtySize(this.stdout);

    const command = this.provider.buildTerminalCommand(
      { args: this.providerArgs, hook: this.hook, cwd: this.cwd },
      process.env,
    );
    const launch = prepareCommandLaunch(
      command.command,
      command.args,
      command.env,
      process.platform,
      this.cwd,
    );
    const child = pty.spawn(launch.command, launch.ptyArgs ?? launch.args, {
      // The bundled ConPTY preserves VT synchronized-output boundaries. The system
      // backend can expose intermediate redraw cursors outside those boundaries.
      // node-pty ships the DLL only for x64/arm64; other architectures keep the system backend.
      ...(process.platform === "win32" && ["x64", "arm64"].includes(process.arch)
        ? { useConptyDll: true }
        : {}),
      name: process.env.TERM ?? "xterm-256color",
      cols,
      rows,
      cwd: this.cwd,
      env: launch.env as Record<string, string>,
    });
    this.child = child;

    // raw mode 仅在 stdin 为 TTY 时开启
    const isInteractive = this.stdin.isTTY === true;
    if (isInteractive) {
      this.stdin.setRawMode(true);
    }
    this.stdin.resume();

    // stdin -> PTY
    this.stdin.on("data", (data: Buffer) => {
      const input = data.toString();
      this.onInput?.(input);
      this.inputCompat.observe(input);
      child.write(input);
    });

    // PTY -> stdout + tap
    child.onData((data: string) => this.handleData(data));

    let appliedCols = cols;
    let appliedRows = rows;
    const applyResize = (newCols: number, newRows: number): void => {
      if (!this.child || (newCols === appliedCols && newRows === appliedRows)) return;
      this.child.resize(newCols, newRows);
      appliedCols = newCols;
      appliedRows = newRows;
      this.onResize?.(newCols, newRows);
    };

    // resize 防抖，50ms 窗口合并快速连续的尺寸变化。timer 持有在实例字段上，cleanup 时
    // clearTimeout 防止 PTY 退出后 callback 还在等待，导致 child.resize 调用已 kill 的子进程。
    this.stdout.on("resize", () => {
      if (this.resizeTimer) clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        if (!this.child) return;
        const { cols: newCols, rows: newRows } = readTtySize(this.stdout);
        applyResize(newCols, newRows);
      }, 50);
    });

    // initialSize 由调用方在建立会话状态时读取。监听挂载后立即校准一次，覆盖尺寸在
    // initialSize 读取与 resize listener 挂载之间发生变化、对应事件因而无法被监听的窗口。
    const currentSize = readTtySize(this.stdout);
    applyResize(currentSize.cols, currentSize.rows);

    // 子进程退出，按 Unix 惯例处理信号退出码，通过回调通知调用方
    child.onExit(({ exitCode, signal }) => {
      if (isInteractive) this.disableRawModeQuiet();
      restoreHostTerminalModes(this.stdout);
      const code = signal ? 128 + signal : exitCode;
      this.onSessionExit?.(code);
    });

    // stdin 结束时写入 EOF 控制字符到 PTY
    this.stdin.on("end", () => {
      child.write("\x04");
    });
  }

  /**
   * PTY 数据到达时的统一处理：OSC 9 修复 + 输出到终端 + 传给 tap
   */
  private handleData(data: string): void {
    // PTY 的 onlcr 会把 OSC 序列里的 \n 转成 \r\n，还原为 \n
    const fixed = data.replace(
      // eslint-disable-next-line no-control-regex
      /\x1b\]9;([\s\S]*?)\x07/g,
      (_, content: string) => `\x1b]9;${content.replace(/\r\n/g, "\n")}\x07`,
    );
    this.stdout.write(fixed);
    this.tap(data);
  }

  // 向 PTY 子进程写入数据，用于远程输入注入
  write(data: string): void {
    this.onInput?.(data);
    this.child?.write(this.inputCompat.encodeRemote(data));
  }

  // 关闭 raw mode 并吞掉 stdin 已关闭场景下的异常。退出 / cleanup 两条路径共用。
  private disableRawModeQuiet(): void {
    try {
      this.stdin.setRawMode(false);
    } catch {
      // stdin 可能已关闭
    }
  }

  cleanup(exitCode: number): void {
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
    if (this.stdin.isTTY) this.disableRawModeQuiet();
    restoreHostTerminalModes(this.stdout);
    if (this.child) {
      try {
        this.child.kill();
      } catch {
        // 子进程可能已退出
      }
      this.child = null;
    }
    this.onSessionExit?.(exitCode);
  }
}
